import React from "react";
import { Form } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import TinymceEditor from "components/base/TinymceEditor";
import Dropzone from "components/custom/CustomDropzone";
import CustomCascader from "components/custom/CustomCascader";
import { Attribute, Option, ProductFormValues } from "../types";
// Import but rename to avoid type conflict
import { FileAttachment as CustomFileAttachment } from "components/custom/types";
import { useTranslation } from "react-i18next";
import { invalidFieldStyles, invalidContainerStyles, standardFieldStyles, themeColors } from "./ProductAttributeFieldStyles";

interface ProductAttributeFieldProps {
  attribute: Attribute;
  formValues: ProductFormValues;
  invalidFields: string[];
  isLoadingOptions: boolean;
  dynamicOptions: { [key: string]: Option[] };
  brandOptions?: Option[];
  onInputChange: (query: string, attribute: Attribute) => void;
  onChange: (attribute_code: string, value: any) => void;
  onFileDrop?: (files: any[]) => void;
  onFileRemove?: (fileName: string) => void;
  onFileUpdate?: (index: number, updatedData: Partial<CustomFileAttachment>) => void;
  onFileIndexChange?: (fromIndex: number, toIndex: number) => void;
  onCategorySelected?: (selectedCategory: any) => void;
  fetchCategories?: () => Promise<any[]>;
  initialCategorySelection?: any;
}

const ProductAttributeField: React.FC<ProductAttributeFieldProps> = ({
  attribute,
  formValues,
  invalidFields,
  isLoadingOptions,
  dynamicOptions,
  brandOptions = [],
  onInputChange,
  onChange,
  onFileDrop,
  onFileRemove,
  onFileUpdate,
  onFileIndexChange,
  onCategorySelected,
  fetchCategories,
  initialCategorySelection,
}) => {
  // Log the initial category selection
  React.useEffect(() => {
    if (initialCategorySelection && attribute.input_type === 'cascader') {
      console.log("[ProductAttributeField] initialCategorySelection for cascader:", initialCategorySelection);
    }
  }, [initialCategorySelection, attribute.input_type]);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  
  const inputValue = formValues[attribute.attribute_code] || "";
  const savedOptions = Array.isArray(formValues[attribute.attribute_code])
    ? formValues[attribute.attribute_code]
    : [];
    
  // Check if this field is marked as invalid
  const isInvalid = invalidFields.includes(attribute.attribute_code);
  
  // Check for numeric fields with invalid values (before the switch statement)
  const numericFields = ["price", "special_price", "weight", "dimensions_de_l_outil_largeur1", 
                        "dimensions_de_l_outil_hauteur1", "longueur25", "quantity_and_stock_status"];
  const isNumericField = numericFields.includes(attribute.attribute_code);
  const numValue = parseFloat(inputValue);
  const hasInvalidNumericValue = isNumericField && inputValue !== "" && !isNaN(numValue) && numValue <= 0;
  
  // Field is invalid if it's in invalidFields array OR if it has a numeric value <= 0
  const fieldIsInvalid = isInvalid || hasInvalidNumericValue;
  
  // Determine which options to use for selects/multiselects
  const combinedOptions =
    attribute.attribute_code === "brand"
      ? brandOptions
      : [
        { option_id: "0", value: "", swatch: "" },
        ...(Array.isArray(dynamicOptions[attribute.attribute_code])
          ? dynamicOptions[attribute.attribute_code]
          : Array.isArray(attribute.options)
            ? attribute.options
            : [])
      ];

  // Render appropriate field based on input_type
  switch (attribute.input_type) {
    case "text":
    case "price":
    case "weight":
    case "number":
      
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            type={attribute.input_type === "text" ? "text" : "number"}
            placeholder={t(attribute.frontend_label)}
            required={attribute.is_required === "1"}
            value={inputValue}
            onChange={(e) => onChange(attribute.attribute_code, e.target.value)}
            className={fieldIsInvalid ? "is-invalid form-control-invalid" : ""}
            style={{
              ...(fieldIsInvalid ? invalidFieldStyles : {}),
              ...(fieldIsInvalid ? {} : standardFieldStyles)
            }}
          />
          {fieldIsInvalid && (
            <div className="invalid-feedback">
              {(() => {
                if (hasInvalidNumericValue) {
                  return `${t(attribute.frontend_label)} must be greater than 0`;
                } else if (attribute.input_type === "price") {
                  return `Please enter a price greater than 0 for ${t(attribute.frontend_label)}`;
                } else if (attribute.input_type === "weight") {
                  return `Please enter a valid number for ${t(attribute.frontend_label)}`;
                } else if (attribute.input_type === "number") {
                  return `Please enter a valid number greater than 0 for ${t(attribute.frontend_label)}`;
                } else {
                  return `${t(attribute.frontend_label)} is required`;
                }
              })()}
            </div>
          )}
        </Form.Group>
      );
      
    case "textarea":
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <div className="tinymce-wrapper">
            <TinymceEditor
              key={`${attribute.attribute_code}`}
              options={{
                height: "15rem",
                placeholder: t(attribute.frontend_label),
                menubar: true,
                toolbar: "bold italic underline link bullist numlist",
                inline: false,
                setup: (editor) => {
                  // Removed auto-focus behavior for description fields
                },
              }}
              value={inputValue}
              onChange={(content) => onChange(attribute.attribute_code, content)}
            />
            {isInvalid && (
              <div className="invalid-feedback" style={{display: 'block'}}>
                {`${t(attribute.frontend_label)} is required`}
              </div>
            )}
          </div>
        </Form.Group>
      );
      
    case "gallery":
      if (!onFileDrop || !onFileRemove || !onFileUpdate || !onFileIndexChange) {
        console.error("Gallery handlers are required for gallery input type");
        return null;
      }
      
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <div>
            <div>
              <Dropzone
                accept={{
                  "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                }}
                onDrop={onFileDrop}
                defaultFiles={(formValues.gallery || []).map((f) => ({
                  name: f.name || '',
                  preview: f.preview || '',
                  size: f.size || 0,
                  resolution: f.resolution || '',
                  altText: f.altText || '',
                  hideFromProductPage: f.hideFromProductPage || false,
                  roles: f.roles || {},
                  originalUrl: f.originalUrl
                }))}
                onFileRemove={onFileRemove}
                onFileUpdate={onFileUpdate}
                onFileIndexChange={onFileIndexChange}
                key={`dropzone-${(formValues.gallery || []).length}`}
              />
            </div>
            {invalidFields.includes("gallery") && (
              <div className="invalid-feedback" style={{display: 'block'}}>
                Product images are required
              </div>
            )}
          </div>
        </Form.Group>
      );
      
    case "cascader": {
      if (!onCategorySelected || !fetchCategories) {
        console.error("Category handlers are required for cascader input type");
        return null;
      }
      
      // Debug category data
      console.log("[ProductAttributeField] Category data:", formValues.category);
      
      // Prepare cascader value
      const cascaderValue = formValues.category ? JSON.stringify({
        level2: formValues.category.level2 && typeof formValues.category.level2 === 'object' ? 
          {
            id: formValues.category.level2.id,
            name: formValues.category.level2.name,
            level: 2,
            include_in_menu: 1
          } : null,
        level3: formValues.category.level3 && typeof formValues.category.level3 === 'object' ? 
          {
            id: formValues.category.level3.id,
            name: formValues.category.level3.name,
            level: 3,
            include_in_menu: 1
          } : null,
        level4: formValues.category.level4 && typeof formValues.category.level4 === 'object' ?
          {
            id: formValues.category.level4.id,
            name: formValues.category.level4.name,
            level: 4,
            include_in_menu: 1
          } : null
      }) : "";
      
      console.log("[ProductAttributeField] Prepared cascader value:", cascaderValue);
      
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <div>
            <CustomCascader
              value={cascaderValue}
              fetchCategories={fetchCategories}
              onCategorySelected={onCategorySelected}
            />
            {invalidFields.includes("category") && (
              <div className="invalid-feedback" style={{display: 'block'}}>
                Category is required
              </div>
            )}
          </div>
        </Form.Group>
      );
    }
      
    case "select":
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <div>
            <Typeahead
              id={attribute.attribute_id}
              className={isInvalid ? "is-invalid form-control-invalid" : ""}
              style={{
                ...(isInvalid ? invalidFieldStyles : {}),
                ...(isInvalid ? {} : standardFieldStyles)
              }}
              labelKey="value"
              options={combinedOptions}
              isLoading={isLoadingOptions}
              placeholder={t(attribute.frontend_label)}
              onInputChange={(query) => {
                if (attribute.options_getter) {
                  onInputChange(query, attribute);
                }
              }}
              selected={Array.isArray(savedOptions) ? savedOptions : []}
              onChange={(selected) => onChange(attribute.attribute_code, selected)}
              renderMenuItemChildren={(option) => {
                const opt = option as Option;
                return (
                  <div className="d-flex align-items-center">
                    {opt.swatch && (
                      <div
                        style={{
                          backgroundColor: opt.swatch,
                          width: "16px",
                          height: "16px",
                          marginRight: "8px",
                          border: "1px solid #ccc",
                        }}
                      />
                    )}
                    <span>{t(opt.value || "")}</span>
                  </div>
                );
              }}
            />
            {isInvalid && (
              <div className="invalid-feedback" style={{display: 'block'}}>
                {`Please select ${t(attribute.frontend_label)}`}
              </div>
            )}
          </div>
        </Form.Group>
      );
      
    case "multiselect":
      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <div>
            <Typeahead
              id={attribute.attribute_id}
              className={isInvalid ? "is-invalid form-control-invalid" : ""}
              style={{
                ...(isInvalid ? invalidFieldStyles : {}),
                ...(isInvalid ? {} : standardFieldStyles)
              }}
              labelKey="value"
              multiple
              options={combinedOptions}
              isLoading={isLoadingOptions}
              placeholder={t(attribute.frontend_label)}
              onInputChange={(query) => {
                if (attribute.options_getter) {
                  onInputChange(query, attribute);
                }
              }}
              selected={Array.isArray(savedOptions) ? savedOptions : []}
              onChange={(selected) => onChange(attribute.attribute_code, selected)}
              renderMenuItemChildren={(option) => {
                const opt = option as Option;
                return (
                  <div className="d-flex align-items-center">
                    {opt.swatch && (
                      <div
                        style={{
                          backgroundColor: opt.swatch,
                          width: "16px",
                          height: "16px",
                          marginRight: "8px",
                          border: "1px solid #ccc",
                        }}
                      />
                    )}
                    <span>{t(opt.value || "")}</span>
                  </div>
                );
              }}
            />
            {isInvalid && (
              <div className="invalid-feedback" style={{display: 'block'}}>
                {`Please select ${t(attribute.frontend_label)}`}
              </div>
            )}
          </div>
        </Form.Group>
      );

    case "readonly": {
      // Get the display value for readonly field
      const getReadonlyDisplayValue = () => {
        // For return policy, show a default value or from formValues
        if (attribute.attribute_code === "politique_de_retour") {
          // Handle object values properly
          if (inputValue && typeof inputValue === 'object' && inputValue.value) {
            return inputValue.value;
          }
          return inputValue || t("30 jours"); // Default value for return policy
        }
        
        // Handle other readonly fields that might be objects
        if (inputValue && typeof inputValue === 'object' && inputValue.value) {
          return inputValue.value;
        }
        
        return inputValue || t("N/A");
      };

      return (
        <Form.Group className="mb-3">
          <Form.Label style={{ textAlign: isArabic ? "right" : "left" }}>
            {t(attribute.frontend_label)}{" "}
            {attribute.is_required === "1" && <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            type="text"
            value={getReadonlyDisplayValue()}
            readOnly
            disabled
            style={{
              backgroundColor: '#f8f9fa',
              borderColor: '#dee2e6',
              color: '#6c757d',
              cursor: 'not-allowed'
            }}
          />
          <Form.Text className="text-muted">
            {attribute.attribute_code === "politique_de_retour" 
              ? t("attributes.returnPolicyRemark") 
              : t("This field is automatically set and cannot be modified")
            }
          </Form.Text>
        </Form.Group>
      );
    }
      
    default:
      return null;
  }
};

export default ProductAttributeField;
